import { useEffect, useMemo, useState } from "react";
import "react-calendar/dist/Calendar.css";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";

import { ProblemAnswer, ProblemType } from "../../types/Rows";
import { formatToDateOnly } from "../lists/formatDate";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Value } from "react-date-picker/dist/cjs/shared/types";
import { deleteProblem, updateTemplateProblemAnswers } from "../../api/reportsApi";
import { DiscussionHistoryInline, EditIcon } from "../atomic/discussionComponent";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../ui/Modal";
import { field } from "../ui/styles";


// 2) make toDate accept ranges too
function toDate(val: Value | string): Date | null {
  if (val == null) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (Array.isArray(val)) {
    const first = val[0];
    return first instanceof Date && !isNaN(first.getTime()) ? first : null;
  }
  if (typeof val === "string") {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Narrow union for answer status used in your radios */
type AnswerStatus = "new" | "ongoing" | "finished";

/* ============================================
   Modal: EditProblemModal
   - Controlled by parent via `open` and `onClose`
   - Calls `onSave(form)` which returns a Promise
============================================ */

export type ProblemForm = {
  answerId: string;
  category: string;
  problemType: ProblemType;
  description: string;
  answer: AnswerStatus;
  personIdentifying: string;
  participantNumber?: string;
  identificationDate: Date | null;
  resolvedDate: Date | null;
};

export function EditProblemModal({
  reportId,
  open,
  onClose,
  initial,
  title = "Edytuj problem",
  onDelete,
}: {
  reportId: string;
  open: boolean;
  onClose: () => void;
  initial: ProblemForm;
  title?: string;
  onDelete?: () => Promise<void>;
}) {
  const [form, setForm] = useState<ProblemForm>(initial);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false); // NEW
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const toProblemAnswer = (f: ProblemForm): ProblemAnswer => ({
    answerId: f.answerId,
    category: f.category,
    problemType: f.problemType,
    description: f.description,
    // your API expects the raw 'answer' field (same as list uses)
    // cast is safe because AnswerStatus aligns with your server enum/string
    answer: (f.answer as any),
    personIdentifying: f.personIdentifying,
    // keep it only for MINOR or leave as empty string if you prefer
    participantNumber:
      f.problemType === ProblemType.MINOR ? (f.participantNumber ?? "") : undefined,
    // send date-only strings (like before with fetch)
    identificationDate: f.identificationDate
      ? formatToDateOnly(f.identificationDate)
      : null,
    resolvedDate: f.resolvedDate ? formatToDateOnly(f.resolvedDate) : null,
  } as unknown as ProblemAnswer);

  // change the mutation to accept a single ProblemAnswer
  const { mutateAsync: saveProblemsAnswers, isSuccess: isSaveProblemSuccess } = useMutation(
    async (answer: ProblemAnswer) => {
      const correlationId =
        (typeof crypto !== "undefined" && "randomUUID" in crypto)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      // API still wants an array — wrap the single answer
      return updateTemplateProblemAnswers(reportId!, [answer], correlationId);
    },
    {
      onSuccess: () => {
        queryClient.resetQueries([reportId, "Problems"]);
      },
    }
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = toProblemAnswer(form);
      await saveProblemsAnswers(payload); // <-- use the mutation here
      // optional: still let external onSave run if someone passed it
      onClose();
    } catch (e: any) {
      setError(e?.message || "Nie udało się zapisać. Spróbuj ponownie.");
    } finally {
      setSaving(false);
    }
  };
      
  // Keep form in sync when opening with different row
  useEffect(() => {
    if (open) {
      setForm({
        ...initial,
        identificationDate: toDate(initial.identificationDate),
        resolvedDate: toDate(initial.resolvedDate),
      });
      setError(null);
    }
  }, [open, initial]);

  const requiresParticipant =
    form.problemType === ProblemType.MINOR;


  const handleDelete = async () => {
    if (!onDelete || saving || deleting) return;
    //if (!confirm("Na pewno chcesz usunąć ten problem?")) return;
    try {
      setDeleting(true);
      await onDelete();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Nie udało się usunąć.");
    } finally {
      setDeleting(false);
    }
  };

  const handleChange = <K extends keyof ProblemForm>(key: K, value: ProblemForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // return (
  //   <Dialog
  //     open={open}
  //     handler={() => {}}
  //     dismiss={{ enabled: !saving }}
  //     size="lg"
  //   >
  //     <DialogHeader className="flex items-start justify-between gap-2 pr-2">
  //       <span className="text-lg">{title}</span>
  //       <IconButton
  //         variant="text"
  //         onClick={() => !saving && onClose()}
  //         disabled={saving}
  //         aria-label="Close"
  //       >
  //         ✕
  //       </IconButton>
  //     </DialogHeader>

  //     <DialogBody className="space-y-4">
  //       {/* Category & Type */}
  //       <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        
  //         <div className="flex flex-col gap-2">
  //           <Typography variant="small" className="opacity-70">
  //             Status
  //           </Typography>
  //           <div className="flex flex-wrap gap-3">
  //             {(["new", "ongoing", "finished"] as AnswerStatus[]).map((s) => (
  //               <label key={s} className="flex items-center gap-2">
  //                 <input
  //                   type="radio"
  //                   name="status"
  //                   value={s}
  //                   checked={form.answer === s}
  //                   onChange={() => handleChange("answer", s)}
  //                   disabled={saving}
  //                 />
  //                 <span className="text-sm">
  //                   {s === "new" ? "Nowe" : s === "ongoing" ? "Trwające" : "Zakończone"}
  //                 </span>
  //               </label>
  //             ))}
  //           </div>
  //         </div>
  //       </div>

  //       {/* Description */}
  //       <Textarea
  //         label="Opis"
  //         value={form.description}
  //         onChange={(e) => handleChange("description", e.target.value)}
  //         rows={5}
  //         maxLength={500}
  //         disabled={saving}
  //       />

  //       {/* People & participant */}
  //       <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
  //         <TextAreaInputShort
  //           label="Osoba identyfikująca"
  //           value={form.personIdentifying}
  //           onChange={(v: string) => handleChange("personIdentifying", v)}
  //         />
  //         {requiresParticipant && (
  //           <TextAreaInputShort
  //             label="Numer uczestnika"
  //             value={form.participantNumber || ""}
  //             onChange={(v: string) => handleChange("participantNumber", v)}
  //             maxLength={64}
  //           />
  //         )}
  //       </div>

  //       {/* Dates */}
  //       <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
  //         <div className="flex flex-col gap-1">
  //           <Typography variant="small" className="opacity-70">
  //             Data identyfikacji
  //           </Typography>
  //           <DatePicker
  //             format="dd/MM/y"
  //             value={form.identificationDate}
  //             onChange={(d) => handleChange("identificationDate", toDate(d))}
  //             clearIcon={null}
  //             calendarIcon={null}
  //             disabled={saving}
  //           />
  //         </div>

  //         <div className="flex flex-col gap-1">
  //           <Typography variant="small" className="opacity-70">
  //             Data zakończenia
  //           </Typography>
  //           <DatePicker
  //             format="dd/MM/y"
  //             value={form.resolvedDate}
  //             onChange={(d) => handleChange("resolvedDate", toDate(d))}
  //             clearIcon={null}
  //             calendarIcon={null}
  //             disabled={saving}
  //           />
  //         </div>
  //       </div>

  //       {saving && (
  //         <div className="flex items-center gap-2">
  //           <Spinner className="h-4 w-4" />
  //           <Typography variant="small">Zapisywanie…</Typography>
  //         </div>
  //       )}
  //       {error && (
  //         <Typography variant="small" color="red">
  //           {error}
  //         </Typography>
  //       )}
  //     </DialogBody>

  //     <DialogFooter className="gap-2 justify-between">
  //       <div>
  //         {onDelete && (
  //           <Button
  //             variant="text"
  //             color="red"
  //             onClick={handleDelete}
  //             disabled={saving || deleting}
  //           >
  //             {deleting ? "Usuwanie…" : "Usuń"}
  //           </Button>
  //         )}
  //       </div>

  //       <div className="flex gap-2">
  //         <Button variant="text" onClick={onClose} disabled={saving || deleting}>
  //           Anuluj
  //         </Button>
  //         <Button onClick={handleSave} disabled={saving || deleting}>
  //           {saving ? "Zapisywanie…" : "Zapisz"}
  //         </Button>
  //       </div>
  //     </DialogFooter>
  //   </Dialog>
  // );



  return (
    <Modal open={open} onClose={() => !saving && onClose()} size="lg">
      <ModalHeader title={title} onClose={() => !saving && onClose()} />

      <ModalBody>
        {/* Status */}
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em]">Status</span>
          <div className="flex gap-2 flex-wrap">
            {(["new", "ongoing", "finished"] as AnswerStatus[]).map((s) => (
              <button
                key={s}
                type="button"
                disabled={saving}
                onClick={() => handleChange("answer", s)}
                className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                  form.answer === s
                    ? s === "finished"
                      ? "bg-[#EDFAF3] text-[#1E8A55] ring-1 ring-[#2BAE72]"
                      : s === "ongoing"
                      ? "bg-[#FFF8ED] text-[#C47A0A] ring-1 ring-[var(--sc-amber)]"
                      : "bg-[var(--sc-bg)] text-[var(--sc-text)] ring-1 ring-[var(--sc-border)]"
                    : "bg-[var(--sc-bg)] text-[var(--sc-muted)] hover:bg-[var(--sc-border)]"
                }`}
              >
                {s === "new" ? "Nowe" : s === "ongoing" ? "Trwające" : "Zakończone"}
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <textarea
          className={field.textarea}
          placeholder="Opis"
          value={form.description}
          onChange={(e) => handleChange("description", e.target.value)}
          rows={5}
          maxLength={500}
          disabled={saving}
        />

        {/* People & participant */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <span className={field.label}>Osoba identyfikująca</span>
            <input
              className={field.input}
              value={form.personIdentifying}
              onChange={(e) => handleChange("personIdentifying", e.target.value)}
            />
          </div>
          {requiresParticipant && (
            <div>
              <span className={field.label}>Numer uczestnika</span>
              <input
                className={field.input}
                value={form.participantNumber || ""}
                onChange={(e) => handleChange("participantNumber", e.target.value)}
                maxLength={64}
              />
            </div>
          )}
        </div>

 {/* Dates */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs opacity-70">
              Data identyfikacji
            </span>
            <DatePicker
              format="dd/MM/y"
              value={form.identificationDate}
              onChange={(d) => handleChange("identificationDate", toDate(d))}
              clearIcon={null}
              calendarIcon={null}
              disabled={saving}
            />
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-xs opacity-70">
              Data zakończenia
            </span>
            <DatePicker
              format="dd/MM/y"
              value={form.resolvedDate}
              onChange={(d) => handleChange("resolvedDate", toDate(d))}
              clearIcon={null}
              calendarIcon={null}
              disabled={saving}
            />
          </div>
        </div>

        {saving && <div className="text-sm opacity-70">Zapisywanie…</div>}
        {error && <div className="text-sm text-red-600">{error}</div>}
      </ModalBody>

      <ModalFooter
      onDelete={onDelete}
      onSave={handleSave}
      />
    </Modal>
  );

}

// Pretty status
const statusLabel = (s?: string) =>
  s === "new" ? "Nowe" : s === "ongoing" ? "Trwające" : s === "finished" ? "Zakończone" : "-";

export function ProblemRowWithEdit({
  input,
  reportId,
  index,
}: {
  input: ProblemAnswer;
  reportId: string;
  index?: number; // optional, if you want to show the row number like before
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  // Safe date display
  const fmt = (d?: string | Date | null) => {
    if (!d) return null;
    const date = d instanceof Date ? d : new Date(d);
    return date.toLocaleDateString();
  };

  const { mutateAsync: removeProblemAsync } = useMutation(
    async ({ reportId, value }: { reportId: string; value: string }) =>
      deleteProblem(reportId, value),
    {
      onSuccess: () => {
        qc.resetQueries([reportId, "Problems"]);
      },
    }
  );

  // Prepare form from row (same as before)
  const initial: ProblemForm = useMemo(
    () => ({
      answerId: input.answerId,
      category: input.category ?? "",
      problemType: input.problemType,
      description: input.description ?? "",
      answer: (input.answer as any) ?? "new",
      personIdentifying: input.personIdentifying ?? "",
      participantNumber: input.participantNumber ?? "",
      identificationDate: input.identificationDate ? new Date(input.identificationDate) : null,
      resolvedDate: input.resolvedDate ? new Date(input.resolvedDate) : null,
    }),
    [input]
  );

  return (
    <>
      {/* === READ-ONLY LIST VIEW === */}
      <div
        className="flex items-start border-t border-[var(--sc-border)] px-4 py-3.5 text-sm hover:bg-[var(--sc-bg)] transition-colors gap-3 odd:bg-[var(--sc-surface)] even:bg-[var(--sc-bg)]"
        key={`problemRow-${input.answerId}`}
      >
        {/* Kategoria (+ participant for MINOR) */}
        <div className="w-[14%] min-w-0 break-words self-start">
          <p className="font-medium text-[var(--sc-text)]">
            {index != null ? `${index}. ` : ""}
            {input.category}
          </p>
          {input.problemType === ProblemType.MINOR && input.participantNumber && (
            <span className="text-xs text-[var(--sc-muted)]">
              nr uczestnika: {input.participantNumber}
            </span>
          )}
        </div>

        {/* Opis */}
        <div className="w-[20%] min-w-0 break-words text-[var(--sc-text)]">{input.description || "-"}</div>

        {/* Status */}
        <div className="w-[10%] min-w-0">
          {input.answer === "finished" ? (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-[#EDFAF3] text-[#1E8A55]">
              {statusLabel(input.answer as any)}
            </span>
          ) : input.answer === "ongoing" ? (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-[#FFF8ED] text-[#C47A0A]">
              {statusLabel(input.answer as any)}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-[var(--sc-bg)] text-[var(--sc-muted)]">
              {statusLabel(input.answer as any)}
            </span>
          )}
        </div>

        {/* Osoba identyfikująca */}
        <div className="w-[16%] min-w-0 break-words text-[var(--sc-muted)]">{input.personIdentifying || "-"}</div>

        {/* Data identyfikacji */}
        <div className="w-[14%] min-w-0 text-[var(--sc-muted)]">{fmt(input?.identificationDate) ?? "-"}</div>

        {/* Data rozwiązania + actions */}
        <div className="w-[14%] min-w-0 text-[var(--sc-muted)]">{fmt(input.resolvedDate) ?? "-"}</div>

        <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
          <DiscussionHistoryInline resourceId={input.answerId} />
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="h-8 w-8 inline-flex items-center justify-center rounded-full text-[var(--sc-muted)] hover:bg-[var(--sc-bg)] hover:text-[var(--sc-text)] transition-colors"
            aria-label="Edytuj"
            title="Edytuj"
          >
            <EditIcon />
          </button>
        </div>
      </div>

    <EditProblemModal
      reportId={reportId}
      open={open}
      onClose={() => setOpen(false)}
      initial={initial}
      // move deletion here (optional)
      onDelete={async () => {
        await removeProblemAsync({ reportId, value: input.answerId });
      }}
      title={`${input.category}`}
    />

    </>
  );
}

export function ProblemsListEditable({
  items,
  reportId,
}: {
  items: ProblemAnswer[];
  reportId: string;
}) {
  // Order: null, new, ongoing, finished (closed)
  const statusOrder = (s: any) => {
    if (s == null || s == "") return 0;
    if (s === "new") return 1;
    if (s === "ongoing") return 2;
    if (s === "finished" || s === "closed") return 3;
    return 4;
  };
  const sorted = [...items].sort((a, b) => statusOrder(a.answer) - statusOrder(b.answer));
  return (
    <>
      {sorted.length === 0 && (
        <p className="text-[13px] text-[var(--sc-muted)] px-4 py-4">Brak znalezisk.</p>
      )}
      {sorted.map((item, idx) => (
        <ProblemRowWithEdit
          key={`editable-problem-${item.answerId}`}
          input={item}
          reportId={reportId}
          index={idx + 1}
        />
      ))}
    </>
  );
}
